import React, { useContext, useEffect, useState } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar';
import { LovDTO, VisitorRecordDetailOutDTO, VisitorRecordInDTO } from '@api/ringil-api.v2';
import { ApiContext } from '@api/api';
import useForm from '@hooks/useForm/useForm';
import * as yup from 'yup';
import useBEValidation from '@hooks/useBEValidation/useBEValidation';
import { ColWithGap, RowWithGap } from '@components/styles.tsx';
import { findValidationMessage } from '@utils/validation.tsx';
import { Field } from '@components/framework/formfield/Field.tsx';
import InputResponsiblePerson from '@components/framework/input/InputResponsiblePerson/InputResponsiblePerson.tsx';
import { InputAndButtonGrid, TwoColGrid } from '@components/ringil3/styles.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import { InputText } from 'primereact/inputtext';
import LinkWithIcon from '@components/ringil3/Links/LinkWithIcon.tsx';
import SvgIconPlus from '@components/framework/orders/IconPlus.tsx';
import DeleteButton from '@components/ringil3/Buttons/DeleteButton.tsx';
import { InputNumber } from '@components/framework/input/InputNumber';
import { Calendar } from 'primereact/calendar';
import { useTranslation } from 'react-i18next';
import AddonsSvg from '@app/icons/AddonsSvg.tsx';
import { shortDateFormat } from '@utils/dateWithTz';
import InputNoteWithLink from '@components/ringil3/Input/InputNoteWithLink/InputNoteWithLink.tsx';
import { Dropdown } from '@components/RingilComponents/SafeDropdown';
import dayjs from 'dayjs';
import { dateToTimeString, timeStringToDate } from '@utils/dateWithTz.tsx';

export interface VisitorRecord {
  duplicate: boolean;
  record?: VisitorRecordDetailOutDTO;
}

interface ComponentProps {
  item?: VisitorRecord;
  onContinue: (id: string) => void;
  visible: boolean;
  setVisible: (visible: boolean) => void;
}

const initialValues: VisitorRecordInDTO = {
  locationId: null,
  labelIds: [],
  responsiblePersonId: null,
  mainContactName: '',
  mainContactId: '',
  mainContactCompany: '',
  car: null,
  note: '',
  additionalContacts: null,
};

const VisitorSidebar: React.FC<ComponentProps> = ({ item, onContinue, visible, setVisible }) => {
  const { tr } = useTranslationLgs();
  const { i18n } = useTranslation();
  const {
    createVisitorRecord,
    updateVisitorRecord,
    getLocationsWithEnabledVisitor,
  } = useContext(ApiContext);
  const { BackendValidationComponent, setBeValidationErrors } = useBEValidation();
  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState<LovDTO[]>([]);
  const [formInitialValues] = useState<VisitorRecordInDTO>(
    item?.record
      ? mapVisitorRecordDetailToInDTO(item.record)
      : initialValues,
  );


  useEffect(() => {
    getLocationsWithEnabledVisitor(
      data => setLocations(data),
    );
  }, []);

  const schema = yup.object().shape({
    locationId: yup.string().required(),
    responsiblePersonId: yup.string().required(),
    mainContactName: yup.string().required(),
    mainContactCompany: yup.string().required(),
    additionalContacts: yup.array().of(
      yup.object().shape({
        contactName: yup.string().required('Guest name is mandatory').min(1, 'Guest name is mandatory'),
        contactId: yup.string().nullable(),
      }),
    ).nullable(),
    car: yup.object().shape({
      carPlate: yup.string().required('Car plate is mandatory'),
      expectedArrivalDate: yup.string().required('Expected arrival date is mandatory'),
      expectedArrivalTime: yup.string().required('Expected arrival time is mandatory'),
      durationMinutes: yup.number().nullable(),
    }).nullable(),
  });

  function mapVisitorRecordDetailToInDTO(record: VisitorRecordDetailOutDTO): VisitorRecordInDTO {
    return {
      locationId: record.locationId ?? null,
      labelIds: record.labels.map(label => label.labelId),
      responsiblePersonId: record.responsiblePersonId ?? null,
      mainContactName: record.visitorName ?? '',
      mainContactId: record.visitorId ?? '',
      mainContactCompany: record.visitorCompany ?? '',
      car: record.car ?? null,
      note: record.note ?? '',
      additionalContacts: record.otherGuests?.map(guest => ({
        contactName: guest.name,
        contactId: guest.visitorId,
      })) ?? null,
    };
  }

  const form = useForm<VisitorRecordInDTO>(
    schema,
    formInitialValues,
    (d) => onContinueHandler(d),
    false,
    false,
    () => {
    },
    'none',
  );

  const [carVisible, setCarVisible] = useState(!!form.values?.car);

  const onContinueHandler = (data: VisitorRecordInDTO) => {
    setLoading(true);
    if (item && !item.duplicate) {
      updateVisitorRecord(
        item.record.id,
        data,
        (data) => {
          setVisible(false);
          setLoading(false);
          onContinue(data.id);
        },
        null,
        {
          onValidationFailed: (errors) => {
            setLoading(false);
            setBeValidationErrors(errors);
          },
        },
      );
    } else {
      createVisitorRecord(
        data,
        (data) => {
          setVisible(false);
          setLoading(false);
          onContinue(data.id);
        },
        null,
        {
          onValidationFailed: (errors) => {
            setLoading(false);
            setBeValidationErrors(errors);
          },
        },
      );
    }
  };

  if (!visible) return null;

  return (
    <>
      <RingilSidebar
        visible={visible}
        onBack={() => setVisible(false)}
        onContinue={() => {
          form.validateAndSend();
        }}
        heading={tr(`VisitorSidebar.heading`, `New visit`)}
        loading={loading}>

        {/* Opts into the legacy PrimeReact overrides in styles/overrides/legacy-fields.scss. */}
        <ColWithGap className={'r-legacy-fields'}>
          <BackendValidationComponent />
          <Field
            label={tr('VisitorSidebar.formLocation', 'Location')}
            required={!form.values.locationId}
            errorMessage={findValidationMessage(form.validationErrors, form.names.locationId, tr)}
          >
            <Dropdown
              value={form.values.locationId}
              options={locations}
              onChange={e => form.setFieldValue('locationId', e.value)}
              optionLabel="name"
              optionValue="id"
              className="w-full"
              filter
            />
          </Field>

          <Field
            label={tr('VisitorSidebar.formResponsiblePerson', 'Responsible person')}
            required={!form.values.responsiblePersonId}
            errorMessage={findValidationMessage(form.validationErrors, form.names.responsiblePersonId, tr)}
          >
            <InputResponsiblePerson
              responsiblePerson={form.values.responsiblePersonId
                ? { id: form.values.responsiblePersonId, name: '', active: true }
                : null}
              onChange={value => form.setFieldValue('responsiblePersonId', value.id)}
            />
          </Field>
          <COHeadline variant={'h4'} title={tr(`VisitorSidebar.formMainContact`, `Main contact`)}></COHeadline>
          <TwoColGrid>
            <Field
              label={tr('VisitorSidebar.formMainContactName', 'Visitor name')}
              required={!form.values.mainContactName}
              errorMessage={findValidationMessage(form.validationErrors, form.names.mainContactName, tr)}
            >
              <InputText
                value={form.values.mainContactName}
                onChange={e => form.setFieldValue(form.names.mainContactName, e.target.value)}
              />
            </Field>
            <Field
              label={tr('VisitorSidebar.formMainContactId', 'Visitor ID')}
              required={false}
              errorMessage={findValidationMessage(form.validationErrors, form.names.mainContactId, tr)}
            >
              <InputText
                value={form.values.mainContactId}
                onChange={e => form.setFieldValue(form.names.mainContactId, e.target.value)}
              />
            </Field>

          </TwoColGrid>

          <Field
            label={tr('VisitorSidebar.formMainContactCompany', 'Visitor company')}
            required={!form.values.mainContactCompany}
            errorMessage={findValidationMessage(form.validationErrors, form.names.mainContactCompany, tr)}
          >
            <InputText
              value={form.values.mainContactCompany}
              onChange={e => form.setFieldValue(form.names.mainContactCompany, e.target.value)}
            />
          </Field>

          {!carVisible && (
            <LinkWithIcon
              label={tr('VisitorSidebar.formCarPlateAdd', 'Car plate')}
              icon={<SvgIconPlus />}
              onClick={() => {
                setCarVisible(true);
                if (!form.values.car) {
                  form.setFieldValue('car', {
                    carPlate: '',
                    expectedArrivalDate: '',
                    expectedArrivalTime: null,
                    durationMinutes: null,
                  });
                }
              }}
            />
          )}

          {carVisible && (
            <>
              <COHeadline variant={'h4'} title={tr(`VisitorSidebar.formCarPlate`, `Car plate`)} />
              <Field
                label={tr('VisitorSidebar.formCarPlate', 'Car plate')}
                required={!form.values.car?.carPlate}
                errorMessage={findValidationMessage(form.validationErrors, 'car.carPlate', tr)}
              >
                <InputAndButtonGrid>
                  <InputText
                    value={form.values.car?.carPlate ?? ''}
                    onChange={e => form.setFieldValue('car', { ...form.values.car, carPlate: e.target.value })}
                  />
                  <RowWithGap>
                    <DeleteButton onClick={() => {
                      form.setFieldValue('car', null);
                      setCarVisible(false);
                    }} />
                  </RowWithGap>
                </InputAndButtonGrid>
              </Field>
              <TwoColGrid>
                <Field
                  label={tr('VisitorSidebar.formCarArrivalDate', 'Expected arrival date')}
                  required={!form.values.car?.expectedArrivalDate}
                  errorMessage={findValidationMessage(form.validationErrors, 'car.expectedArrivalDate', tr)}
                >
                  <Calendar
                    locale={i18n.language}
                    selectionMode={'single'}
                    value={form.values.car?.expectedArrivalDate ? new Date(form.values.car.expectedArrivalDate) : null}
                    onChange={e =>
                      form.setFieldValue('car', {
                        ...form.values.car,
                        expectedArrivalDate: e.value && e.value instanceof Date ? dayjs(e.value).format('YYYY-MM-DD') : null,
                      })
                    }
                    showIcon
                    icon={<AddonsSvg width={20} height={20} />}
                    dateFormat={shortDateFormat}
                    readOnlyInput
                    showButtonBar={true}
                    showWeek={true}
                  />
                </Field>
                <Field
                  label={tr('VisitorSidebar.formCarArrivalTime', 'Expected arrival time')}
                  required={!form.values.car?.expectedArrivalTime}
                  errorMessage={findValidationMessage(form.validationErrors, 'car.expectedArrivalTime', tr)}
                >
                  <Calendar
                    value={timeStringToDate(form.values.car?.expectedArrivalTime)}
                    onChange={e =>
                      form.setFieldValue('car', {
                        ...form.values.car,
                        expectedArrivalTime: e.value && e.value instanceof Date ? dateToTimeString(e.value) : null,
                      })
                    }
                    timeOnly
                  />
                </Field>
              </TwoColGrid>
              <Field
                label={tr('VisitorSidebar.formCarDuration', 'Duration')}
                required={false}
                errorMessage={findValidationMessage(form.validationErrors, 'car.duration', tr)}
              >
                <InputNumber
                  value={form.values.car?.durationMinutes}
                  min={0}
                  onChange={e =>
                    form.setFieldValue('car', {
                      ...form.values.car,
                      durationMinutes: e.value,
                    })
                  }
                />
              </Field>
            </>
          )}


          {(form.values.additionalContacts && form.values.additionalContacts.length > 0) && (
            <COHeadline variant={'h4'} title={tr('VisitorSidebar.formAdditionalContacts', 'Additional guests')} />
          )}
          {(form.values.additionalContacts ?? []).map((contact, idx) => (
            <TwoColGrid key={idx}>
              <Field
                label={tr('VisitorSidebar.formAdditionalContactName', 'Guest name')}
                required={!contact.contactName}
                errorMessage={findValidationMessage(form.validationErrors, `additionalContacts[${idx}].contactName`, tr)}
              >
                <InputText
                  value={contact.contactName ?? ''}
                  onChange={e => {
                    const updated = [...(form.values.additionalContacts ?? [])];
                    updated[idx] = { ...contact, contactName: e.target.value };
                    form.setFieldValue('additionalContacts', updated);
                  }}
                />
              </Field>
              <Field
                label={tr('VisitorSidebar.formAdditionalContactId', 'Guest ID')}
                required={false}
                errorMessage={findValidationMessage(form.validationErrors, `additionalContacts[${idx}].contactId`, tr)}
              >
                <InputAndButtonGrid>
                  <InputText
                    value={contact.contactId ?? ''}
                    onChange={e => {
                      const updated = [...(form.values.additionalContacts ?? [])];
                      updated[idx] = { ...contact, contactId: e.target.value };
                      form.setFieldValue('additionalContacts', updated);
                    }}
                  />
                  <RowWithGap>
                    <DeleteButton onClick={() => {
                      const updated = [...(form.values.additionalContacts ?? [])];
                      updated.splice(idx, 1);
                      form.setFieldValue('additionalContacts', updated.length ? updated : null);
                    }} />
                  </RowWithGap>
                </InputAndButtonGrid>
              </Field>

            </TwoColGrid>
          ))}

          <LinkWithIcon
            label={tr('VisitorSidebar.formAdditionalContactAdd', 'Add guest')}
            icon={<SvgIconPlus />}
            onClick={() => {
              const updated = [...(form.values.additionalContacts ?? [])];
              updated.push({ contactName: '', contactId: '' });
              form.setFieldValue('additionalContacts', updated);
            }}
          />

          <InputNoteWithLink
            note={form.values.note}
            onChanges={v => {
              form.setFieldValue('note', v);
            }}
            noteLabel={tr(`VisitorSidebar.formNote`, `Note`)}
          />
        </ColWithGap>
      </RingilSidebar>
    </>);
};

export const useVisitorSidebar = () => {
  const [visible, setVisible] = useState(false);
  return { VisitorSidebar, setVisible, visible };
};
